import type {
  BomOperationPayload,
  BomVersion,
  BomVersionPayload,
  EngineeringProduct,
} from './types'

export interface BomEditorMaterial {
  material_id?: number
  material_name: string
  material_sku: string
  material_unit: string
  quantity: number
  loss_percentage: number
}

export interface BomEditorOperation {
  operation_sequence: number
  operation_reference_id?: number
  operation_name: string
  department_agent_id?: number
  equipment_reference_id?: number
  equipment_name: string
  duration_minutes: number
  cost_per_produced_unit_day: number
  cost_per_produced_unit_night: number
  materials: BomEditorMaterial[]
}

export interface BomEditorForm {
  description: string
  operations: BomEditorOperation[]
}

export type BomEditorErrors = Record<string, string>

export function emptyBomOperation(sequence: number): BomEditorOperation {
  return {
    operation_sequence: sequence,
    operation_name: '',
    equipment_name: '',
    duration_minutes: 1,
    cost_per_produced_unit_day: 0.01,
    cost_per_produced_unit_night: 0.01,
    materials: [],
  }
}

export function standardSocksOperations(): BomEditorOperation[] {
  return ['Knitting', 'Linking / toe closing', 'Boarding', 'Quality inspection', 'Packaging']
    .map((operation_name, index) => ({
      ...emptyBomOperation(index + 1),
      operation_name,
    }))
}

export function bomVersionToEditorForm(version: BomVersion): BomEditorForm {
  return {
    description: version.description || '',
    operations: version.operations.map((operation) => ({
      operation_sequence: operation.operation_sequence,
      operation_reference_id: operation.operation_reference_id ?? undefined,
      operation_name: operation.operation_name,
      department_agent_id: operation.department_agent_id ?? undefined,
      equipment_reference_id: operation.equipment_reference_id ?? undefined,
      equipment_name: operation.equipment_name || '',
      duration_minutes: Number(operation.duration_minutes),
      cost_per_produced_unit_day: Number(
        operation.cost_per_produced_unit_day ?? operation.cost_per_produced_unit,
      ),
      cost_per_produced_unit_night: Number(
        operation.cost_per_produced_unit_night
          ?? operation.cost_per_produced_unit_day
          ?? operation.cost_per_produced_unit,
      ),
      materials: operation.materials.map((material) => ({
        material_id: material.material_id,
        material_name: material.material_name,
        material_sku: material.material_sku,
        material_unit: material.material_unit,
        quantity: Number(material.quantity),
        loss_percentage: Number(material.loss_percentage),
      })),
    })),
  }
}

export function validateBomEditor(form: BomEditorForm, productId: number): BomEditorErrors {
  const errors: BomEditorErrors = {}
  if (form.description.trim().length > 500) errors.description = '500 ta belgidan oshirmang.'
  if (!form.operations.length) errors.operations = 'Kamida bitta ishlab chiqarish operatsiyasini qo‘shing.'

  const sequences = new Set<number>()
  let materialCount = 0
  form.operations.forEach((operation, operationIndex) => {
    const prefix = `operations.${operationIndex}`
    if (!Number.isInteger(operation.operation_sequence) || operation.operation_sequence < 1) {
      errors[`${prefix}.operation_sequence`] = 'Tartib musbat butun son bo‘lishi kerak.'
    } else if (sequences.has(operation.operation_sequence)) {
      errors[`${prefix}.operation_sequence`] = 'Tartib takrorlanmasligi kerak.'
    }
    sequences.add(operation.operation_sequence)
    if (!operation.operation_reference_id && !operation.operation_name.trim()) {
      errors[`${prefix}.operation_name`] = 'Operatsiyani tanlang yoki nomini kiriting.'
    }
    if (!operation.department_agent_id) errors[`${prefix}.department_agent_id`] = 'Bo‘limni tanlang.'
    if (!(Number(operation.duration_minutes) > 0)) errors[`${prefix}.duration_minutes`] = 'Noldan katta vaqt kiriting.'
    const dayRate = Number(operation.cost_per_produced_unit_day)
    const nightRate = Number(operation.cost_per_produced_unit_night)
    if (!Number.isFinite(dayRate) || dayRate < 0) {
      errors[`${prefix}.cost_per_produced_unit_day`] = 'Narx manfiy bo‘lishi mumkin emas.'
    }
    if (!Number.isFinite(nightRate) || nightRate < 0) {
      errors[`${prefix}.cost_per_produced_unit_night`] = 'Narx manfiy bo‘lishi mumkin emas.'
    }
    if (!(dayRate > 0) && !(nightRate > 0)) {
      errors[`${prefix}.cost_per_produced_unit_day`] = 'Kunduzgi yoki tungi smena narxini kiriting.'
    }

    const materialIds = new Set<number>()
    operation.materials.forEach((material, materialIndex) => {
      materialCount += 1
      const materialPrefix = `${prefix}.materials.${materialIndex}`
      if (!material.material_id) errors[`${materialPrefix}.material_id`] = 'Xomashyoni tanlang.'
      else if (material.material_id === productId) errors[`${materialPrefix}.material_id`] = 'Mahsulot o‘zini xomashyo sifatida sarflay olmaydi.'
      else if (materialIds.has(material.material_id)) errors[`${materialPrefix}.material_id`] = 'Ushbu operatsiyada xomashyo takrorlangan.'
      if (material.material_id) materialIds.add(material.material_id)
      if (!(Number(material.quantity) > 0)) errors[`${materialPrefix}.quantity`] = 'Miqdor noldan katta bo‘lishi kerak.'
      const loss = Number(material.loss_percentage)
      if (!Number.isFinite(loss) || loss < 0 || loss > 100) {
        errors[`${materialPrefix}.loss_percentage`] = 'Yo‘qotish 0% va 100% oralig‘ida bo‘lishi kerak.'
      }
    })
  })
  if (!materialCount) errors.materials = 'Yo‘nalishga kamida bitta xomashyo qo‘shing.'
  return errors
}

export function buildBomPayload(
  form: BomEditorForm,
  productId: number,
): BomVersionPayload {
  const operations: BomOperationPayload[] = form.operations
    .slice()
    .sort((left, right) => left.operation_sequence - right.operation_sequence)
    .map((operation) => ({
      operation_sequence: Number(operation.operation_sequence),
      ...(operation.operation_reference_id
        ? { operation_reference_id: operation.operation_reference_id }
        : { operation_name: operation.operation_name.trim() }),
      department_agent_id: Number(operation.department_agent_id),
      ...(operation.equipment_reference_id
        ? { equipment_reference_id: operation.equipment_reference_id }
        : operation.equipment_name.trim()
          ? { equipment_name: operation.equipment_name.trim() }
          : {}),
      duration_minutes: Number(operation.duration_minutes),
      cost_per_produced_unit_day: Number(operation.cost_per_produced_unit_day) || 0,
      cost_per_produced_unit_night: Number(operation.cost_per_produced_unit_night) || 0,
      materials: operation.materials.map((material) => ({
        material_id: Number(material.material_id),
        quantity: Number(material.quantity),
        loss_percentage: Number(material.loss_percentage),
      })),
    }))
  return {
    product_id: productId,
    ...(form.description.trim() ? { description: form.description.trim() } : {}),
    operations,
  }
}

export function setEditorMaterial(
  operation: BomEditorOperation,
  product: EngineeringProduct,
): void {
  operation.materials.push({
    material_id: product.id,
    material_name: product.name,
    material_sku: product.sku,
    material_unit: product.unit,
    quantity: 0.001,
    loss_percentage: 0,
  })
}

export function effectiveMaterialQuantity(material: BomEditorMaterial): number {
  return Number(material.quantity || 0) * (1 + Number(material.loss_percentage || 0) / 100)
}

export function bomEditorTotals(form: BomEditorForm) {
  return {
    operations: form.operations.length,
    materials: form.operations.reduce((total, operation) => total + operation.materials.length, 0),
    durationMinutes: form.operations.reduce((total, operation) => total + Number(operation.duration_minutes || 0), 0),
    operationCost: form.operations.reduce((total, operation) => total + Number(operation.cost_per_produced_unit_day || 0), 0),
    operationCostNight: form.operations.reduce((total, operation) => total + Number(operation.cost_per_produced_unit_night || 0), 0),
  }
}
